import React, {
  useEffect,
  useState,
} from "react";

import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

import { Address } from "api/Community/CommunityInterfaces";


interface MapLeftDrawerProps {
  allAddresses: Address[];

  communityAPI: {
    getAll: () => Promise<any[]>;
  };

  getOutputNodes: (
    communityId: number
  ) => Promise<any[]>;

  onAddressesChange: (
    addresses: Address[]
  ) => void;
}


const MapLeftDrawer: React.FC<
  MapLeftDrawerProps
> = ({
  allAddresses,
  communityAPI,
  getOutputNodes,
  onAddressesChange,
}) => {
  const [
    communities,
    setCommunities,
  ] = useState<any[]>([]);

  const [
    selectedCommunity,
    setSelectedCommunity,
  ] = useState<any | null>(
    null
  );

  const [
    outputNodes,
    setOutputNodes,
  ] = useState<any[]>([]);

  const [
    selectedOutputNode,
    setSelectedOutputNode,
  ] = useState<any | null>(
    null
  );

  const [
    loadingCommunities,
    setLoadingCommunities,
  ] = useState(false);

  const [
    loadingOutputs,
    setLoadingOutputs,
  ] = useState(false);


  /* -------------------------------------------------------------------------- */
  /* Load communities                                                           */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    let alive = true;

    setLoadingCommunities(true);

    communityAPI
      .getAll()
      .then((response) => {
        if (!alive) {
          return;
        }

        setCommunities(
          response ?? []
        );
      })
      .catch((error) => {
        console.error(
          "Error loading communities:",
          error
        );

        if (alive) {
          setCommunities([]);
        }
      })
      .finally(() => {
        if (alive) {
          setLoadingCommunities(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [
    communityAPI,
  ]);


  /* -------------------------------------------------------------------------- */
  /* Load output nodes when community changes                                   */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    if (!selectedCommunity) {
      setOutputNodes([]);
      setSelectedOutputNode(null);

      return;
    }


    let alive = true;

    setLoadingOutputs(true);

    setOutputNodes([]);

    setSelectedOutputNode(
      null
    );


    getOutputNodes(
      selectedCommunity.id
    )
      .then((response) => {
        if (!alive) {
          return;
        }


        /*
         * Same shape as your
         * addCommunityToNode functionality:
         *
         * response = [
         *   {
         *     nodes: [...]
         *   }
         * ]
         */

        const nodes =
          response.flatMap(
            (group: any) =>
              group.nodes ?? []
          );


        setOutputNodes(
          nodes
        );
      })
      .catch((error) => {
        console.error(
          "Error loading community output nodes:",
          error
        );

        if (alive) {
          setOutputNodes([]);
        }
      })
      .finally(() => {
        if (alive) {
          setLoadingOutputs(false);
        }
      });


    return () => {
      alive = false;
    };
  }, [
    selectedCommunity,
    getOutputNodes,
  ]);


  /* -------------------------------------------------------------------------- */
  /* All addresses                                                              */
  /* -------------------------------------------------------------------------- */

  const handleAllAddresses =
    () => {
      setSelectedCommunity(
        null
      );

      setSelectedOutputNode(
        null
      );

      setOutputNodes([]);

      onAddressesChange(
        allAddresses
      );
    };


  /* -------------------------------------------------------------------------- */
  /* Select output node                                                         */
  /* -------------------------------------------------------------------------- */

  const handleOutputNodeSelect =
    (node: any) => {
      setSelectedOutputNode(
        node
      );


      /*
       * Output nodes already contain
       * address IDs, just like your
       * addCommunityToNode function
       * receives people IDs.
       */

      const addressIds =
        new Set<number>(
          (
            node.addresses ??
            []
          )
            .map(
              (address: any) =>
                typeof address ===
                "object"
                  ? Number(
                      address.id
                    )
                  : Number(
                      address
                    )
            )
            .filter(
              (id: number) =>
                Number.isFinite(
                  id
                )
            )
        );


      const filteredAddresses =
        allAddresses.filter(
          (address) =>
            addressIds.has(
              Number(
                address.id
              )
            )
        );


      onAddressesChange(
        filteredAddresses
      );
    };


  /* -------------------------------------------------------------------------- */
  /* Render                                                                     */
  /* -------------------------------------------------------------------------- */

  return (
    <Box
      sx={{
        px: 2,
        py: 2,
      }}
    >
      {/* -------------------------------------------------------------------- */}
      {/* Heading                                                              */}
      {/* -------------------------------------------------------------------- */}

      <Typography
        variant="h6"
        sx={{
          mb: 0.5,
          fontWeight: 600,
        }}
      >
        Map Addresses
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2,
        }}
      >
        Choose which addresses to display.
      </Typography>


      {/* -------------------------------------------------------------------- */}
      {/* All Addresses                                                        */}
      {/* -------------------------------------------------------------------- */}

      <List
        disablePadding
        sx={{
          mb: 2,
        }}
      >
        <ListItemButton
          selected={
            selectedCommunity ===
              null &&
            selectedOutputNode ===
              null
          }
          onClick={
            handleAllAddresses
          }
          sx={{
            borderRadius: 1.5,

            px: 1.5,

            "&.Mui-selected": {
              backgroundColor:
                "action.selected",
            },

            "&.Mui-selected:hover": {
              backgroundColor:
                "action.selected",
            },
          }}
        >
          <PublicOutlinedIcon
            sx={{
              mr: 1.5,
            }}
          />

          <ListItemText
            primary="All Addresses"
            secondary={`${allAddresses.length} addresses`}
          />
        </ListItemButton>
      </List>


      <Divider
        sx={{
          mb: 2.5,
        }}
      />

      
      {/* -------------------------------------------------------------------- */}
      {/* Community selector                                                   */}
      {/* -------------------------------------------------------------------- */}

      {/*
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontWeight: 600,
        }}
      >
        Community
      </Typography>


      {loadingCommunities ? (
        <Box
          sx={{
            display: "flex",
            justifyContent:
              "center",
            py: 2,
          }}
        >
          <CircularProgress
            size={22}
          />
        </Box>
      ) : (
        <Autocomplete
          options={
            communities
          }
          value={
            selectedCommunity
          }
          onChange={(
            _,
            value
          ) => {
            setSelectedCommunity(
              value
            );


            setSelectedOutputNode(
              null
            );

            onAddressesChange(
              allAddresses
            );
          }}
          getOptionLabel={(
            community
          ) =>
            community?.name ??
            ""
          }
          isOptionEqualToValue={(
            option,
            value
          ) =>
            option.id ===
            value.id
          }
          renderInput={(
            params
          ) => (
            <TextField
              {...params}
              label="Select Community"
              size="small"
            />
          )}
        />
      )}


      {selectedCommunity && (
        <Box
          sx={{
            mt: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
            }}
          >
            Output
          </Typography>


          {loadingOutputs ? (
            <Box
              sx={{
                display:
                  "flex",

                justifyContent:
                  "center",

                py: 2,
              }}
            >
              <CircularProgress
                size={22}
              />
            </Box>
          ) : (
            <List
              disablePadding
              dense
            >
              {outputNodes.map(
                (node: any) => {
                  const addressCount =
                    node.addresses_count ??
                    node.address_count ??
                    node.addresses
                      ?.length ??
                    0;


                  return (
                    <ListItemButton
                      key={
                        node.id
                      }
                      selected={
                        selectedOutputNode
                          ?.id ===
                        node.id
                      }
                      onClick={() =>
                        handleOutputNodeSelect(
                          node
                        )
                      }
                      sx={{
                        borderRadius:
                          1.5,

                        mb:
                          0.5,

                        px:
                          1.5,

                        "&.Mui-selected":
                          {
                            backgroundColor:
                              "action.selected",
                          },

                        "&.Mui-selected:hover":
                          {
                            backgroundColor:
                              "action.selected",
                          },
                      }}
                    >
                      <LocationOnOutlinedIcon
                        fontSize="small"
                        sx={{
                          mr:
                            1.5,

                          opacity:
                            0.75,
                        }}
                      />

                      <ListItemText
                        primary={
                          node.name
                        }
                        secondary={`${addressCount} addresses`}
                      />
                    </ListItemButton>
                  );
                }
              )}


              {outputNodes.length ===
                0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    px:
                      1,

                    py:
                      1,
                  }}
                >
                  No output nodes found
                </Typography>
              )}
            </List>
          )}
        </Box>
      )}

      */}
    </Box>
  );
};


export default MapLeftDrawer;